import { z } from 'zod';
import { sectionHasValue } from '../util';
import { registry } from '../registry';
export const respiratoryCardTitles = {
    smokingHistory: 'Smoking History',
    breathingPattern: 'SOB, Breathing Pattern & Effort',
    lungAuscultation: 'Lung Auscultation',
    cough: 'Cough',
    cpapBipap: 'CPAP / BiPAP',
    mechanicalVentilator: 'Mechanical Ventilator',
    tracheostomy: 'Tracheostomy',
    treatments: 'Respiratory Treatments & Medications',
};
const lungSoundOptions = [
    { value: 'diminish', label: 'Diminish' },
    { value: 'wheeze', label: 'Wheezes' },
    { value: 'rales', label: 'Rales' },
    { value: 'rhonchi', label: 'Rhonchi' },
    { value: 'crackles', label: 'Crackles' },
    { value: 'clear', label: 'Clear' },
    { value: 'absent', label: 'Absent' },
];
const papMaskOptions = [
    { value: 'full face mask', label: 'Full face mask' },
    { value: 'nasal mask', label: 'Nasal mask' },
    { value: 'nasal pillows', label: 'Nasal pillows' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'total face mask', label: 'Total face mask' },
];
const papComplianceOptions = [
    { value: 'nightly — compliant', label: 'Nightly — compliant' },
    { value: 'intermittent use', label: 'Intermittent use' },
    { value: 'not using — refuses', label: 'Not using — refuses' },
    { value: 'not using — mask intolerance', label: 'Not using — mask intolerance' },
    { value: 'device malfunction — reported', label: 'Device malfunction — reported' },
];
export const baseLungAuscultationSchema = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    upperRight: z
        .string()
        .optional()
        .register(registry, { label: 'Upper Right', options: [...lungSoundOptions] }),
    upperLeft: z
        .string()
        .optional()
        .register(registry, { label: 'Upper Left', options: [...lungSoundOptions] }),
    midRight: z
        .string()
        .optional()
        .register(registry, { label: 'Mid Right', options: [...lungSoundOptions] }),
    midLeft: z
        .string()
        .optional()
        .register(registry, { label: 'Mid Left', options: [...lungSoundOptions] }),
    lowerRight: z
        .string()
        .optional()
        .register(registry, { label: 'Lower Right', options: [...lungSoundOptions] }),
    lowerLeft: z
        .string()
        .optional()
        .register(registry, { label: 'Lower Left', options: [...lungSoundOptions] }),
    details: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Details',
        options: [{ value: 'prn', label: 'PRN' }],
    }),
})
    .register(registry, { label: respiratoryCardTitles.lungAuscultation });
export const baseLungAuscultationSchemaRefined = baseLungAuscultationSchema.refine((data) => sectionHasValue(data), {
    message: 'Mark as within normal limits if none applies',
    path: [],
});
export const baseBreathingPatternSchema = z
    .object({
    shortness: z
        .string()
        .optional()
        .register(registry, {
        label: 'Shortness of Breath',
        options: [
            { value: 'at rest', label: 'At Rest' },
            { value: 'on exertion', label: 'Exertion only' },
            { value: 'minimal exertion', label: 'Minimal exertion' },
            { value: 'orthopnea', label: 'Orthopnea (lying flat)' },
            { value: 'absent', label: 'Absent' },
        ],
    }),
    mrcScale: z
        .string()
        .optional()
        .register(registry, {
        label: 'Scale',
        options: [
            { value: '1', label: '1 — strenuous only' },
            { value: '2', label: '2 — hurrying / slight hill' },
            { value: '3', label: '3 — slower than peers' },
            { value: '4', label: '4 — stops at ~100 yards' },
            { value: '5', label: '5 — too breathless to leave home' },
        ],
    }),
    breathingPattern: z
        .string()
        .optional()
        .register(registry, {
        label: 'Breathing Pattern',
        options: [
            { value: 'regular and unlabored', label: 'Regular and unlabored' },
            { value: 'labored', label: 'Labored' },
            { value: 'shallow', label: 'Shallow' },
            { value: 'rapid (tachypnea)', label: 'Rapid (tachypnea)' },
            { value: 'slow (bradypnea)', label: 'Slow (bradypnea)' },
            { value: 'cheyne-stokes', label: 'Cheyne-Stokes' },
            { value: 'kussmaul', label: 'Kussmaul (DKA)' },
        ],
    }),
    accessoryMuscleUse: z
        .string()
        .optional()
        .register(registry, {
        label: 'Accessory Muscle Use / Effort',
        options: [
            { value: 'none — unlabored', label: 'None — unlabored' },
            { value: 'mild — nasal flaring / intercostal', label: 'Mild — nasal flaring / intercostal' },
            { value: 'moderate — intercostal retractions', label: 'Moderate — intercostal retractions' },
            { value: 'severe — supraclavicular tug', label: 'Severe — supraclavicular tug' },
        ],
    }),
    pursedLipBreathing: z
        .string()
        .optional()
        .register(registry, {
        label: 'Pursed-Lip Breathing in use',
        options: [
            { value: 'no', label: 'No' },
            { value: 'yes — patient managing', label: 'Yes — patient managing' },
            { value: 'yes — taught this visit', label: 'Yes — taught this visit' },
        ],
    }),
})
    .register(registry, { label: respiratoryCardTitles.breathingPattern });
export const baseCpapBipapSchema = z
    .object({
    papTherapy: z
        .string()
        .optional()
        .register(registry, {
        label: 'PAP therapy ordered',
        options: [
            { value: 'none', label: 'None' },
            { value: 'cpap', label: 'CPAP' },
            { value: 'bipap', label: 'BiPAP' },
            { value: 'avaps / asv', label: 'AVAPS / ASV' },
        ],
    }),
    cpapPressure: z.string().optional().register(registry, { label: 'CPAP pressure (cmH₂O)' }),
    cpapMaskType: z
        .string()
        .optional()
        .register(registry, { label: 'Mask / interface type', options: [...papMaskOptions] }),
    cpapCompliance: z
        .string()
        .optional()
        .register(registry, { label: 'Compliance and tolerance', options: [...papComplianceOptions] }),
    cpapAdherence: z.string().optional().register(registry, { label: 'Device data / adherence hours (if available)' }),
    bipapIpap: z.string().optional().register(registry, { label: 'IPAP (cmH₂O)' }),
    bipapEpap: z.string().optional().register(registry, { label: 'EPAP (cmH₂O)' }),
    bipapBackupRate: z.string().optional().register(registry, { label: 'Backup rate (if set)' }),
    bipapMaskType: z
        .string()
        .optional()
        .register(registry, { label: 'Mask / interface type', options: [...papMaskOptions] }),
    bipapCompliance: z
        .string()
        .optional()
        .register(registry, { label: 'Compliance and tolerance', options: [...papComplianceOptions] }),
    bipapAdherence: z.string().optional().register(registry, { label: 'Device data / adherence hours (if available)' }),
})
    .register(registry, { label: respiratoryCardTitles.cpapBipap });
export const baseMechanicalVentilatorSchema = z
    .object({
    ventilatorInUse: z
        .string()
        .optional()
        .register(registry, {
        label: 'Ventilator in use',
        options: [
            { value: 'no', label: 'No' },
            { value: 'yes — ventilator dependent', label: 'Yes — ventilator dependent' },
        ],
    }),
    ventMode: z
        .string()
        .optional()
        .register(registry, {
        label: 'Vent mode',
        options: [
            { value: 'ac/vc', label: 'AC/VC' },
            { value: 'ac/pc', label: 'AC/PC' },
            { value: 'simv', label: 'SIMV' },
            { value: 'psv', label: 'PSV' },
            { value: 'cpap via vent', label: 'CPAP via vent' },
            { value: 'prvc', label: 'PRVC' },
        ],
    }),
    tidalVolume: z.string().optional().register(registry, { label: 'Tidal volume (mL)' }),
    fio2: z.string().optional().register(registry, { label: 'FiO₂ (%)' }),
    peep: z.string().optional().register(registry, { label: 'PEEP (cmH₂O)' }),
    setRate: z.string().optional().register(registry, { label: 'Set rate (breaths/min)' }),
    pressureSupport: z.string().optional().register(registry, { label: 'Pressure support (cmH₂O)' }),
    patientTriggeredBreaths: z
        .string()
        .optional()
        .register(registry, {
        label: 'Patient-triggered breaths?',
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
        ],
    }),
    alarms: z
        .string()
        .optional()
        .register(registry, {
        label: 'Alarms — status at this visit',
        options: [
            { value: 'no alarms — within parameters', label: 'No alarms — within parameters' },
            { value: 'high pressure alarm — resolved', label: 'High pressure alarm — resolved' },
            { value: 'low pressure / disconnect — resolved', label: 'Low pressure / disconnect — resolved' },
            { value: 'apnea alarm — assessed', label: 'Apnea alarm — assessed' },
            { value: 'alarm unresolved — md notified', label: 'Alarm unresolved — MD notified' },
        ],
    }),
    settingsMatchOrders: z
        .string()
        .optional()
        .register(registry, {
        label: 'Settings match physician orders?',
        options: [
            { value: 'yes — confirmed', label: 'Yes — confirmed' },
            { value: 'no — md notified', label: 'No — MD notified' },
        ],
    }),
    ventCircuit: z
        .string()
        .optional()
        .register(registry, {
        label: 'Vent circuit / tubing — inspection',
        options: [
            { value: 'intact — no issues', label: 'Intact — no issues' },
            { value: 'condensate drained — intact', label: 'Condensate drained — intact' },
            { value: 'leak detected — corrected', label: 'Leak detected — corrected' },
            { value: 'circuit changed this visit', label: 'Circuit changed this visit' },
        ],
    }),
})
    .register(registry, { label: respiratoryCardTitles.mechanicalVentilator });
export const baseTracheostomySchema = z
    .object({
    tracheostomyPresent: z
        .string()
        .optional()
        .register(registry, {
        label: 'Tracheostomy present',
        options: [
            { value: 'none', label: 'None' },
            { value: 'yes', label: 'Yes' },
        ],
    }),
    tubeType: z
        .string()
        .optional()
        .register(registry, {
        label: 'Tube type / brand',
        options: [
            { value: 'cuffed', label: 'Cuffed' },
            { value: 'cuffless', label: 'Cuffless' },
            { value: 'fenestrated', label: 'Fenestrated' },
            { value: 'speaking valve in place', label: 'Speaking valve in place' },
            { value: 'capped — weaning', label: 'Capped — weaning' },
        ],
    }),
    tubeSize: z.string().optional().register(registry, { label: 'Tube size (Fr or mm ID)' }),
    lastTubeChange: z.string().optional().register(registry, { label: 'Date of last tube change' }),
    stomaSiteAssessment: z
        .string()
        .optional()
        .register(registry, {
        label: 'Stoma site assessment',
        options: [
            { value: 'clean and intact', label: 'Clean and intact' },
            { value: 'slight erythema — cleaned', label: 'Slight erythema — cleaned' },
            { value: 'granulation tissue', label: 'Granulation tissue' },
            { value: 'infection signs — md notified', label: 'Infection signs — MD notified' },
            { value: 'skin breakdown — treated', label: 'Skin breakdown — treated' },
        ],
    }),
    secretionManagement: z
        .string()
        .optional()
        .register(registry, {
        label: 'Secretion management',
        options: [
            { value: 'minimal — clear', label: 'Minimal — clear' },
            { value: 'moderate — white / clear', label: 'Moderate — white / clear' },
            { value: 'thick — suctioning required', label: 'Thick — suctioning required' },
            { value: 'colored — yellow / green', label: 'Colored — yellow / green' },
            { value: 'bloody — md notified', label: 'Bloody — MD notified' },
        ],
    }),
    innerCannula: z
        .string()
        .optional()
        .register(registry, {
        label: 'Inner cannula — this visit',
        options: [
            { value: 'cleaned and replaced', label: 'Cleaned and replaced' },
            { value: 'disposable — replaced', label: 'Disposable — replaced' },
            { value: 'not due — assessed only', label: 'Not due — assessed only' },
        ],
    }),
    suctioning: z
        .string()
        .optional()
        .register(registry, {
        label: 'Suctioning performed?',
        options: [
            { value: 'no — not needed', label: 'No — not needed' },
            { value: 'yes — open', label: 'Yes — open' },
            { value: 'yes — inline', label: 'Yes — inline' },
            { value: 'yes — taught caregiver', label: 'Yes — taught caregiver' },
        ],
    }),
    trachCareBy: z
        .string()
        .optional()
        .register(registry, {
        label: 'Trach care performed by',
        options: [
            { value: 'nurse only', label: 'Nurse only' },
            { value: 'patient self-care — independent', label: 'Patient self-care — independent' },
            { value: 'patient with nurse assist', label: 'Patient with nurse assist' },
            { value: 'caregiver — adequate technique', label: 'Caregiver — adequate technique' },
            { value: 'caregiver — technique reinforced', label: 'Caregiver — technique reinforced' },
        ],
    }),
})
    .register(registry, { label: respiratoryCardTitles.tracheostomy });
export const baseSmokingHistorySchema = z
    .object({
    smokingStatus: z
        .string()
        .optional()
        .register(registry, {
        label: 'Smoking Status',
        options: [
            { value: 'current', label: 'Current' },
            { value: 'former', label: 'Former' },
            { value: 'never', label: 'Never' },
        ],
    }),
    packYears: z.string().optional().register(registry, { label: 'Pack-Years' }),
    quitYear: z.string().optional().register(registry, { label: 'Quit Year' }),
    vape: z
        .string()
        .optional()
        .register(registry, {
        label: 'Vape',
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
        ],
    }),
})
    .register(registry, { label: respiratoryCardTitles.smokingHistory });
export const baseRespiratoryTreatmentsSchema = z
    .object({
    nebulizerTreatment: z
        .string()
        .optional()
        .register(registry, {
        label: 'Nebulizer treatment',
        options: [
            { value: 'not ordered', label: 'Not ordered' },
            { value: 'administered — tolerated well', label: 'Administered — tolerated well' },
            { value: 'self-administered — technique correct', label: 'Self-administered — technique correct' },
            { value: 'administered — technique corrected', label: 'Administered — technique corrected' },
            { value: 'not administered — refused', label: 'Not administered — refused' },
        ],
    }),
    nebulizerTreatmentNotes: z.string().optional().register(registry, { label: 'Nebulizer notes' }),
    mdiInhaler: z
        .string()
        .optional()
        .register(registry, {
        label: 'MDI / Inhaler',
        options: [
            { value: 'not ordered', label: 'Not ordered' },
            { value: 'technique correct', label: 'Technique correct' },
            { value: 'technique reinforced this visit', label: 'Technique reinforced this visit' },
            { value: 'not using as prescribed', label: 'Not using as prescribed' },
        ],
    }),
    mdiInhalerNotes: z.string().optional().register(registry, { label: 'MDI / Inhaler notes' }),
    incentiveSpirometry: z
        .string()
        .optional()
        .register(registry, {
        label: 'Incentive spirometry',
        options: [
            { value: 'not ordered', label: 'Not ordered' },
            { value: 'performed — goal volume achieved', label: 'Performed — goal volume achieved' },
            { value: 'performed — below goal', label: 'Performed — below goal' },
            { value: 'unable to perform', label: 'Unable to perform' },
            { value: 'refusing — educated', label: 'Refusing — educated' },
        ],
    }),
    chestPt: z
        .string()
        .optional()
        .register(registry, {
        label: 'Chest PT / airway clearance',
        options: [
            { value: 'not ordered', label: 'Not ordered' },
            { value: 'performed — tolerated', label: 'Performed — tolerated' },
            { value: 'vest / oscillation device in use', label: 'Vest / oscillation device in use' },
            { value: 'postural drainage performed', label: 'Postural drainage performed' },
        ],
    }),
})
    .register(registry, { label: respiratoryCardTitles.treatments });
export const baseCoughSchema = z
    .object({
    cough: z
        .string()
        .optional()
        .register(registry, {
        label: 'Cough',
        options: [
            { value: 'no', label: 'No' },
            { value: 'dry', label: 'Dry' },
            { value: 'productive', label: 'Productive' },
        ],
    }),
    color: z
        .string()
        .optional()
        .register(registry, {
        label: 'Color',
        options: [
            { value: 'clear', label: 'Clear' },
            { value: 'streaked', label: 'Streaked' },
            { value: 'white', label: 'White' },
            { value: 'yellow', label: 'Yellow' },
            { value: 'green', label: 'Green' },
            { value: 'red', label: 'Red' },
        ],
    }),
    amount: z
        .string()
        .optional()
        .register(registry, {
        label: 'Amount',
        options: [
            { value: 'small', label: 'Small' },
            { value: 'medium', label: 'Medium' },
            { value: 'large', label: 'Large' },
        ],
    }),
})
    .register(registry, { label: respiratoryCardTitles.cough });
